import IApiCommand from '@/api/IApiCommand';
import RetryManager from '@/api/RetryManager';
import DisplayItem from '@/models/DisplayItem';
import IPersistenceCommandCreator from './IPersistenceCommandCreator';
import IPersistenceEventHandler from './IPersistenceEventHandler';
import PersistenceEventType from './PersistenceEventType';

export class PersistenceManager {
  private commandQueue: Array<IApiCommand<unknown>> = new Array<IApiCommand<unknown>>();
  private retryManager: RetryManager = new RetryManager();
  private processingCommand = false;
  private unrecoverableError = false;
  private _shutdownMode = false;
  private persistenceEventHandlers: Array<IPersistenceEventHandler> = new Array<IPersistenceEventHandler>();
  private persistenceCommandCreators: Array<IPersistenceCommandCreator<unknown>> = new Array<
    IPersistenceCommandCreator<unknown>
  >();
  private _currentEventType: PersistenceEventType = PersistenceEventType.COMPLETE;

  addPersistenceCommandCreator(persistenceCommandCreator: IPersistenceCommandCreator<unknown> | null): void {
    if (persistenceCommandCreator && this.persistenceCommandCreators.indexOf(persistenceCommandCreator) === -1) {
      this.persistenceCommandCreators.push(persistenceCommandCreator);
    }
  }

  addPersistenceEventHandler(value: IPersistenceEventHandler) {
    if (this.persistenceEventHandlers.indexOf(value) === -1) {
      this.persistenceEventHandlers.push(value);
    }
  }

  save(item: DisplayItem | null, navigationEvent: boolean, ignoreSessionData = false, shutDownMode = false): void {
    if (this._shutdownMode) {
      this.notifyPersistenceEventHandlers(PersistenceEventType.FINAL);
      return;
    }

    if (navigationEvent) {
      this.notifyPersistenceEventHandlers(PersistenceEventType.SAVING);
    }

    this.persistenceCommandCreators.forEach((creator) => {
      const command: IApiCommand<unknown> | null = creator.getCommand(item, ignoreSessionData);
      if (command) {
        this.addApiCommand(command);
      }
    });

    this._shutdownMode = shutDownMode;

    if (!this.processingCommand && this.commandQueue.length === 0) {
      this.notifyPersistenceEventHandlers(
        this._shutdownMode ? PersistenceEventType.FINAL : PersistenceEventType.COMPLETE
      );
    }
  }

  private addApiCommand(command: IApiCommand<unknown>): void {
    this.commandQueue.push(command);
    this.processCommand();
  }

  reset() {
    this.persistenceCommandCreators.splice(0, this.persistenceCommandCreators.length);
    this.commandQueue.splice(0, this.commandQueue.length);
    this.processingCommand = false;
    this.unrecoverableError = false;
    this._currentEventType = PersistenceEventType.COMPLETE;
  }

  private processCommand(): void {
    if (this.unrecoverableError || this.processingCommand) {
      return;
    }

    if (this.commandQueue.length === 0) {
      this.notifyPersistenceEventHandlers(
        this._shutdownMode ? PersistenceEventType.FINAL : PersistenceEventType.COMPLETE
      );
      return;
    }

    this.processingCommand = true;
    const command = this.commandQueue[0];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    this.retryManager
      .runCommand(command)
      .then(() => {
        that.removeCommand(command);
        that.processingCommand = false;
        that.processCommand();
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .catch((error) => {
        that.unrecoverableError = true;
        that.processingCommand = false;
        that.notifyPersistenceEventHandlers(PersistenceEventType.ERROR);
      });
  }

  private removeCommand(command: IApiCommand<unknown>): void {
    const index = this.commandQueue.indexOf(command, 0);
    if (index > -1) {
      this.commandQueue.splice(index, 1);
    }
  }

  get currentEventType(): PersistenceEventType {
    return this._currentEventType;
  }

  private notifyPersistenceEventHandlers(type: PersistenceEventType) {
    this._currentEventType = type;
    this.persistenceEventHandlers.forEach((handler) => handler.persistenceEvent(type));
  }
}

export default PersistenceManager;
